import { MouseEvent, MouseEventHandler } from "react";

import { TextProvider } from "../../../context/Text";
import { TypographyProvider, useTypography } from "../../../context/Typography";
import { useGetLinkComponentAndProps } from "../../../hooks/link";
import { useContentColor } from "../../../styles/colors/content/useContentColor";
import { ContentEmphasis, KeyColorRole } from "../../../styles/colors/types";
import {
  typographyBodyLineHeightMap,
  TypographyBodySize,
  typographyBodySizeMap,
  TypographyLineHeight
} from "../../../styles/typography/typography";
import getLinkStyles from "../utils/getLinkStyles";

import type { FontWeight, LocationShape, Path } from "../../../types";

type Props = {
  children: React.ReactNode | string;
  to: string | LocationShape | Path;
  newTab?: boolean;
  onClick?: MouseEventHandler;
  disabled?: boolean;
  size?: TypographyBodySize;
  lineHeight?: TypographyLineHeight;
  navLink?: boolean;
  exact?: boolean;
  replace?: boolean;
  tone?: KeyColorRole;
  emphasis?: ContentEmphasis;
  onHighEmphasisSurface?: boolean;
  disableInteractiveStyles?: boolean;
  underline?: boolean;
  fontWeight?: FontWeight;
  state?: any;
};

export default function Link({
  children,
  to,
  onClick,
  newTab,
  disabled = false,
  size: sizeProp,
  lineHeight: lineHeightProp,
  navLink,
  exact,
  replace,
  tone = "primary",
  emphasis = "high",
  onHighEmphasisSurface = false,
  disableInteractiveStyles = false,
  underline = false,
  fontWeight = "bold",
  state
}: Props) {
  const color = useContentColor({ tone, emphasis, onHighEmphasisSurface });

  const styleProps = getLinkStyles(
    disabled,
    disableInteractiveStyles,
    underline,
    fontWeight
  );

  const { Component, linkProps } = useGetLinkComponentAndProps({
    to,
    disabled,
    newTab,
    navLink,
    styleProps,
    replace,
    state
  });

  const {
    typographyBodySize: typographyBodySizeFromContext,
    lineHeight: lineHeightFromContext
  } = useTypography();

  const size = sizeProp || typographyBodySizeFromContext || "medium";
  const lineHeight = lineHeightProp || lineHeightFromContext || "small";

  return (
    <Component
      target={newTab ? "_blank" : undefined}
      onClick={(e: MouseEvent) => {
        !disabled && onClick && onClick(e);
      }}
      {...typographyBodySizeMap[size]}
      {...typographyBodyLineHeightMap[lineHeight]}
      exact={exact}
      {...linkProps}
      {...styleProps}
      color={color}
    >
      <TextProvider value>
        <TypographyProvider value={{ typographyBodySize: size }}>
          {children}
        </TypographyProvider>
      </TextProvider>
    </Component>
  );
}
